<template>
  <VTable
    :items="computedItems"
    :fields="fields"
    :checked-rows="checkedItems"
    :loading="loading"
    :on-toggle-row="onToggleRow"
    :on-toggle-all="onToggleAll"
    :on-input-search="onInputSearch"
    :on-edit-item="onEditItem"
    :on-delete-item="onDeleteItem"
    :on-click-to-add="onClickToAdd"
    searchPlaceholder="Search for user...."
    hover
    striped
    border
    small
    fixed
    dark
  >
    <template #footer>
      <VUserManagerTablePagination
        class="user-manager-card__footer-pagination"
      />
    </template>
  </VTable>
</template>
<script>
import {
  createUserManagerMixin,
  userManagerStore
} from "@/mixins/userManagerMixin";
import { authStore, createAuthStoreMixin } from "@/mixins/authStoreMixin";

import VUserManagerTablePagination from "./VUserManagerTablePagination";
import { debounce } from "@/utils/debounce";
import ModalType from "@/utils/dictionaries/modalType";
import { getUser, getUserRole } from "@/utils/validation/user";
import moment from "moment";

export default {
  name: "VUserManagerTable",

  mixins: [createUserManagerMixin(), createAuthStoreMixin()],

  components: {
    VUserManagerTablePagination
  },

  data: () => {
    return {
      loading: false,
      lastSearchTerm: null,
      searchFn: () => {}
    };
  },

  computed: {
    isReload() {
      return this[authStore.getterTypes.GETTER_PAGE_RELOAD];
    },
    fields() {
      return [
        // { key: "check", _classes: "checkbox" },
        {
          key: "user_id",
          label: "ID",
          _style: "min-width: 35px;max-width: 50px"
        },
        { key: "user_avatar", label: "Avatar", _style: "min-width: 65px" },
        {
          key: "user_username",
          label: "Username",
          _style: "min-width: 125px",
          _classes: "username"
        },
        {
          key: "user_first_name",
          label: "First Name",
          _style: "min-width: 125px",
          _classes: "user_first_name"
        },
        {
          key: "user_last_name",
          label: "Last Name",
          _style: "min-width: 125px",
          _classes: "user_last_name"
        },
        {
          key: "user_email",
          label: "User Mail",
          _style: "min-width: 150px",
          _classes: "user-mail"
        },
        {
          key: "user_phone_number",
          label: "Phone",
          _style: "min-width: 150px",
          _classes: "user_phone_number"
        },
        { key: "carListing", label: "Car Listing", _style: "min-width: 210px" },
        {
          key: "user_role",
          label: "Role",
          _style: "min-width: 100px",
          _classes: "user-role"
        },
        {
          key: "user_created_at",
          label: "Date Created",
          _style: "min-width: 170px",

        },
        {
          key: "last_comment",
          label: "Last Comment",
          _style: "min-width: 310px"
        },
        
        { key: "action", _style: "max-width: 20px;min-width:20px" }
      ];
    },
    checkedItems() {
      return this[userManagerStore.getterTypes.GETTER_CHECKED_ROWS];
    },
    items() {
      return this[userManagerStore.getterTypes.GETTER_USERS];
    },
    itemsCount() {
      return this[userManagerStore.getterTypes.GETTER_USERS_COUNT];
    },
    computedItems() {
      return this.items && this.items.map(item => {
        return {
          ...item,
          user_id: item.id,
          user_avatar: item.user_avatar,
          user_username: item.user_username,
          user_email: item.user_email,
          user_phone_number: item.user_phone_number || '',
          user_role: getUserRole(item.user_role),
          text_role: getUserRole(item),
          user_isManager: true,
          user_bio: item.bio,
          user_password: item.user_password,
          last_comment: item.last_comment,
          carListing: item.carListing,
          user_created_at: item.user_created_at ? moment(item.user_created_at).format('lll') : '',
        }
      })
    }
  },

  watch: {
    isReload(newValue){
      this.loadData();
    },
    $route() {
      this.loadData();
    }
  },

  async created() {
    this[userManagerStore.actionTypes.ACTION_JOIN_TO_USER_MANAGER]();
    await this.loadData();

    this.searchFn = debounce.call(
      this,
      async term => {
        const query = {
          ...this.$route.query,
          search: term
        };

        if (!query.search && query.offset) {
          query.offset = 0;
        }

        this.$router
          .push({
            query
          })
          .catch(() => {});
      },
      200
    );
  },

  methods: {
    getLastUsers() {
      return this.items
        .map(item => getUser(item))
        .map(u => ({ created_at: u.created_at, id: u.id }));
    },
    onToggleRow({ item }) {
      this[userManagerStore.mutationTypes.SET_CHECKED_ROW](item);
    },
    onToggleAll(items) {
      this[userManagerStore.mutationTypes.SET_CHECKED_ROWS](items);
    },
    onInputSearch(t) {
      this.searchFn(t);
    },
    onEditItem(item) {
      this.$modals.open(ModalType.CreateUserModal, item)
      this[userManagerStore.mutationTypes.UNSET_CHECKED_ROWS]()
    },
    onDeleteItem(item) {
      const users = [...this.checkedItems]
        .map(r => getUser(r)),
        usernames = users.map(u => u.username).join(', ')

      const ids = users.map(u => u.id)
        .filter((id, index, arr) => index === arr.indexOf(id));

      const lastUsers = this.getLastUsers().filter(u => !ids.includes(u.id));

      const deleteAction = () => {
        this[userManagerStore.actionTypes.ACTION_DELETE_USER]({
          users: ids,
          lastUsers
        });

        if (ids.length === this.items.length) {
          this.$router.push({
            query: ""
          });
        }

        this[userManagerStore.mutationTypes.UNSET_CHECKED_ROWS]();
        this.$modals.close(ModalType.ConfirmationModal)
      }

      this.$modals.open(ModalType.ConfirmationModal, {
        title: 'Deletion',
        body: `Are you sure you want to delete <strong>${ usernames }</strong> ${ users.length > 1 ? 'users' : 'user' }?`,
        buttonText: 'Delete',
        onClickButton: deleteAction
      })
    },
    onClickToAdd() {
      this.$modals.open(ModalType.CreateUserModal);
    },
    async loadData() {
      this[userManagerStore.mutationTypes.UNSET_CHECKED_ROWS]();
      this.loading = true;
      await this[userManagerStore.actionTypes.ACTION_LOAD_USERS]({
        limit: this.$route.query.limit,
        offset: this.$route.query.offset,
        search: this.$route.query.search
      });
      await this[authStore.mutationTypes.MUTATION_SET_PAGE_RELOAD](false)

      this.loading = false;
    }
  }
};
</script>
